import { slotStarts, zonedParts } from "./slots";
import type { EventState, Level, Participant } from "./types";

export interface WindowScore {
  start: number;
  end: number;
  available: string[];
  missing: string[];
  score: number;
  preferredCount: number;
  ifNeedBeCount: number;
  requiredMissing: string[];
}

export interface OptionTally {
  optionId: string;
  love: number;
  fine: number;
  no: number;
  acceptable: number;
  score: number;
}

export function rankWindows(state: EventState, limit = 5): WindowScore[] {
  const ev = state.event;
  const slots = slotStarts(ev);
  const n = ev.durationMin / ev.slotMin;
  if (!Number.isInteger(n) || n < 1) return [];

  const byDate = new Map<string, number[]>();
  for (const s of slots) {
    const { date } = zonedParts(s, ev.timezone);
    const arr = byDate.get(date) ?? [];
    arr.push(s);
    byDate.set(date, arr);
  }

  const candidates: WindowScore[] = [];
  for (const daySlots of byDate.values()) {
    for (let i = 0; i + n <= daySlots.length; i++) {
      const windowSlots = daySlots.slice(i, i + n);
      if (!isRealConsecutive(windowSlots, ev.slotMin, ev.timezone)) continue;
      const start = windowSlots[0];
      if (start === undefined) continue;
      const scored = scoreWindow(state, windowSlots, start, start + ev.durationMin);
      if (scored.available.length === 0) continue;
      candidates.push(scored);
    }
  }

  candidates.sort((a, b) => {
    const rm = a.requiredMissing.length - b.requiredMissing.length;
    if (rm !== 0) return rm;
    const av = b.available.length - a.available.length;
    if (av !== 0) return av;
    const sc = b.score - a.score;
    if (sc !== 0) return sc;
    return a.start - b.start;
  });

  const picked: WindowScore[] = [];
  const half = ev.durationMin / 2;
  for (const w of candidates) {
    if (picked.some((p) => overlapMinutes(p, w) >= half)) continue;
    picked.push(w);
    if (picked.length >= limit) break;
  }
  return picked;
}

export function scoreWindowAt(
  state: EventState,
  start: number,
  durationMin: number,
): WindowScore | null {
  const ev = state.event;
  const slots = slotStarts(ev);
  const n = durationMin / ev.slotMin;
  if (!Number.isInteger(n) || n < 1) return null;
  const idx = slots.indexOf(start);
  if (idx < 0) return null;
  const windowSlots: number[] = [];
  for (let i = 0; i < n; i++) {
    const s = slots[idx + i];
    if (s === undefined) return null;
    windowSlots.push(s);
  }
  if (!isRealConsecutive(windowSlots, ev.slotMin, ev.timezone)) return null;
  return scoreWindow(state, windowSlots, start, start + durationMin);
}

export function tallyPoll(state: EventState, pollId: string): OptionTally[] {
  const poll = state.event.polls.find((p) => p.id === pollId);
  if (!poll) return [];
  const tallies: OptionTally[] = poll.options.map((o) => ({
    optionId: o.id,
    love: 0,
    fine: 0,
    no: 0,
    acceptable: 0,
    score: 0,
  }));
  for (const p of state.participants) {
    const votes = p.pollVotes[pollId] ?? {};
    for (const t of tallies) {
      const v = votes[t.optionId];
      if (v === 2) t.love += 1;
      else if (v === 1) t.fine += 1;
      else if (v === 0) t.no += 1;
    }
  }
  for (const t of tallies) {
    t.acceptable = t.love + t.fine;
    t.score = 2 * t.love + t.fine;
  }
  const order = new Map(poll.options.map((o, i) => [o.id, i]));
  tallies.sort((a, b) => {
    if (b.acceptable !== a.acceptable) return b.acceptable - a.acceptable;
    if (b.score !== a.score) return b.score - a.score;
    if (a.no !== b.no) return a.no - b.no;
    return (order.get(a.optionId) ?? 0) - (order.get(b.optionId) ?? 0);
  });
  return tallies;
}

function isRealConsecutive(windowSlots: number[], slotMin: number, tz: string): boolean {
  for (let i = 1; i < windowSlots.length; i++) {
    const prevSlot = windowSlots[i - 1];
    const curSlot = windowSlots[i];
    if (prevSlot === undefined || curSlot === undefined) return false;
    if (curSlot - prevSlot !== slotMin) return false;
    const prev = zonedParts(prevSlot, tz);
    const cur = zonedParts(curSlot, tz);
    if (prev.date !== cur.date) return false;
  }
  return true;
}

function overlapMinutes(a: WindowScore, b: WindowScore): number {
  const s = Math.max(a.start, b.start);
  const e = Math.min(a.end, b.end);
  return Math.max(0, e - s);
}

function levelAt(p: Participant, slot: number): Level {
  const v = p.availability[String(slot)];
  if (v === 1 || v === 2 || v === 3) return v;
  return 0;
}

function scoreWindow(
  state: EventState,
  windowSlots: number[],
  start: number,
  end: number,
): WindowScore {
  const available: string[] = [];
  const missing: string[] = [];
  const requiredMissing: string[] = [];
  let score = 0;
  let preferredCount = 0;
  let ifNeedBeCount = 0;
  for (const p of state.participants) {
    let min: Level = 3;
    for (const s of windowSlots) {
      const lv = levelAt(p, s);
      if (lv < min) min = lv;
    }
    if (min < 1) {
      missing.push(p.name);
      if (p.required) requiredMissing.push(p.name);
    } else {
      available.push(p.name);
      score += min;
      if (min === 3) preferredCount += 1;
      if (min === 1) ifNeedBeCount += 1;
    }
  }
  return {
    start,
    end,
    available,
    missing,
    score,
    preferredCount,
    ifNeedBeCount,
    requiredMissing,
  };
}
